<template>
  <div v-if="hasAccess" class="app-ai-tracking pb-10">
    <page-submenu :links="links" />

    <PageContent>
      <section class="space-y-6">
        <div class="ai-domain-filter">
          <div class="ai-domain-filter-field">
            <span class="ai-domain-filter-dot" :style="domainColorStyle(activeAiDomain)"></span>
            <div class="ai-domain-filter-control">
              <span>Domaine</span>
              <t-rich-select
                class="ai-domain-select"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                :value="selectedDomainUuid"
                :disabled="isDomainLoading"
                @change="selectDomain"
              >
                <template slot="label" slot-scope="{ className, options }">
                  <span :class="className">{{ selectedDomainLabel(options) }}</span>
                </template>
                <template slot="option" slot-scope="{ option }">
                  <div class="ai-domain-option">
                    <span class="ai-domain-filter-dot" :style="domainColorStyle(option.raw)"></span>
                    <span>{{ option.raw.label }}</span>
                  </div>
                </template>
              </t-rich-select>
            </div>
          </div>
        </div>

        <router-view :ai-domain="activeAiDomain"></router-view>
      </section>
    </PageContent>
  </div>

  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-500">Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent'
import PageSubmenu from '@/components/PageSubmenu.vue'

const ALL_DOMAINS_UUID = '__all__'
const ALL_DOMAINS_OPTION = {
  uuid: ALL_DOMAINS_UUID,
  label: 'Tous les domaines',
  color: '#64748b',
  isAllDomains: true
}
const ALL_DOMAINS_CONTEXT = {
  label: ALL_DOMAINS_OPTION.label,
  color: ALL_DOMAINS_OPTION.color,
  isAllDomains: true
}

export default {
  name: 'app-ai-home',
  components: {
    PageContent,
    PageSubmenu
  },
  data () {
    return {
      isDomainLoading: false,
      domainOptions: [ALL_DOMAINS_OPTION],
      selectedAiDomainUuid: ALL_DOMAINS_UUID
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => ['admin', 'community-manager'].includes(role))
    },
    links () {
      return [
        {
          label: 'Suivi',
          path: '/app/dashboard/tracking/ai/tracking',
          roles: ['admin', 'community-manager']
        },
        {
          label: 'Base de connaissances',
          path: '/app/dashboard/tracking/ai/knowledge',
          roles: ['admin', 'community-manager']
        },
        {
          label: 'Quotas',
          path: '/app/dashboard/tracking/ai/quotas',
          roles: ['admin', 'community-manager']
        },
        {
          label: 'Assistant test',
          path: '/app/dashboard/tracking/ai/assistant-test',
          roles: ['admin', 'community-manager']
        }
      ]
    },
    selectedDomainUuid () {
      return this.selectedAiDomainUuid
    },
    activeAiDomain () {
      if (this.selectedAiDomainUuid === ALL_DOMAINS_UUID) {
        return ALL_DOMAINS_CONTEXT
      }

      return this.domainOptions.find(domain => domain.uuid === this.selectedAiDomainUuid) || ALL_DOMAINS_CONTEXT
    },
    activeAiDomainLabel () {
      return this.activeAiDomain.label || ALL_DOMAINS_OPTION.label
    }
  },
  async created () {
    if (!Array.isArray(this.roles) || this.roles.length === 0) {
      try {
        await this.$store.dispatch('FETCH_ROLES')
      } catch (error) {
        this.$router.replace('/app/dashboard')
        return
      }
    }

    if (!this.hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    this.loadDomains()
  },
  methods: {
    loadDomains () {
      this.isDomainLoading = true
      http.get('/domain').then(({ data }) => {
        const domains = Array.isArray(data) ? data : []
        const enabledDomains = domains.filter(domain => domain?.application?.slug !== 'disabled')
        this.domainOptions = [ALL_DOMAINS_OPTION, ...enabledDomains]
        this.selectedAiDomainUuid = ALL_DOMAINS_UUID
      }).catch(err => {
        console.log(err)
        this.domainOptions = [ALL_DOMAINS_OPTION]
        this.selectedAiDomainUuid = ALL_DOMAINS_UUID
      }).finally(() => {
        this.isDomainLoading = false
      })
    },
    selectDomain (domainUuid) {
      if (!this.domainOptions.some(item => item.uuid === domainUuid)) {
        return
      }

      this.selectedAiDomainUuid = domainUuid
    },
    selectedDomainLabel (options) {
      const selected = Array.isArray(options) && options.length > 0 ? options[0]?.raw : null
      return selected?.label || this.activeAiDomainLabel
    },
    domainColorStyle (domain) {
      return { backgroundColor: domain?.color || '#2563eb' }
    }
  }
}
</script>

<style scoped>
.ai-domain-filter {
  display: flex;
  justify-content: flex-end;
}

.ai-domain-filter-field {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 22rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.75rem 0.9rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.ai-domain-filter-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.16);
}

.ai-domain-filter-control {
  flex: 1 1 auto;
  min-width: 0;
}

.ai-domain-filter-control > span {
  display: block;
  margin-bottom: 0.25rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.1;
}

.ai-domain-select {
  width: 100%;
}

.ai-domain-option {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.35rem 0.25rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .ai-domain-filter {
    justify-content: stretch;
  }

  .ai-domain-filter-field {
    max-width: none;
  }
}
</style>
